import { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, Stack, Typography, Button, TextField, Chip, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  CircularProgress, IconButton,
} from '@mui/material';
import {
  Close as CloseIcon, Edit as CorrectIcon,
  TrendingUp as UpIcon, TrendingDown as DownIcon,
  RemoveCircleOutline as FlatIcon, Warning as WarnIcon,
} from '@mui/icons-material';
import API from '../../BaseURL';
import swal from '../../utils/swal';

// ═══════════════════════════════════════════════════════════════════
//  Correcting one day, after it has already been approved.
//
//  The money is in the cash book, so this is never an edit: the receipt
//  is reversed and a new one raised. The dialog's whole job is to say
//  what that will do to the cash BEFORE it happens, because "change
//  PAID to CREDIT" and "take GH¢ 12.00 out of the till" are the same
//  action and only one of them sounds like it.
// ═══════════════════════════════════════════════════════════════════

const TNUM = { fontVariantNumeric: 'tabular-nums' };
const money = (n) => Number(n || 0).toLocaleString(undefined,
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const prettyType = (t) => String(t || '')
  .replace(/_+$/, '').replace(/_/g, ' ')
  .replace(/\b\w/g, (c) => c.toUpperCase());

const longDate = (d) => /^\d{4}-\d{2}-\d{2}$/.test(d || '')
  ? new Date(`${d}T00:00:00`).toLocaleDateString(undefined,
      { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
  : (d || '');

// The same four the teacher's sheet writes, so a correction can never
// produce a state the teacher could not have produced themselves.
const CHOICES = [
  { status: 'full',    word: 'PAID',      hint: 'Took it and paid in full',
    bg: '#e8f6ed', fg: '#15803d', line: '#b9e3ca' },
  { status: 'partial', word: 'PART PAID', hint: 'Paid some of it; the rest is owed',
    bg: '#eaf2fb', fg: '#1c5b96', line: '#c6dcf1' },
  { status: 'none',    word: 'CREDIT',    hint: 'Took it, paid nothing',
    bg: '#fdf2e0', fg: '#a1610a', line: '#f3ddb8' },
  { status: 'absent',  word: 'NOT EATEN', hint: 'Did not take it — nothing owed',
    bg: '#fdeaea', fg: '#b91c1c', line: '#f6cccc' },
];

const MIN_REASON = 10;

export default function CorrectCellDialog({
  open, onClose, cell, studentId, studentName, className,
  incomeType, date, rate, onDone,
}) {
  const [status, setStatus] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [win, setWin] = useState(null);

  const wasStatus = cell?.status || null;
  const wasAmount = Number(cell?.amount_paid || 0);
  const wasPosted = String(cell?.approval_status || '').toUpperCase() === 'APPROVED';
  const feeRate = Number(cell?.daily_amount || rate || 0);

  useEffect(() => {
    if (!open) return;
    setStatus(wasStatus || 'full');
    setAmount(String((wasStatus === 'absent' || wasStatus === 'none' ? 0 : wasAmount).toFixed(2)));
    setReason('');
    API.get('/collections/corrections/window')
      .then(r => setWin(r.data || null))
      .catch(() => setWin(null));
  }, [open, wasStatus, wasAmount]);

  // Picking a status fills the amount with what that status usually
  // means, so the common case is one press.
  const pick = (s) => {
    setStatus(s);
    if (s === 'absent' || s === 'none') setAmount('0.00');
    else if (s === 'full') setAmount(feeRate.toFixed(2));
    else if (s === 'partial' && Number(amount) >= feeRate) setAmount((feeRate / 2).toFixed(2));
  };

  const willBe = (status === 'absent' || status === 'none') ? 0 : (Number(amount) || 0);
  const cashEffect = wasPosted ? willBe - wasAmount : willBe;
  const changed = status !== wasStatus || Math.abs(willBe - wasAmount) > 0.009;

  const problem = useMemo(() => {
    if (!changed) return 'Nothing has been changed yet.';
    if (status === 'partial' && willBe <= 0) {
      return 'A part payment of nothing is a CREDIT — choose that instead.';
    }
    if (status === 'partial' && feeRate > 0 && willBe >= feeRate) {
      return `A part payment has to be less than the ${money(feeRate)} rate — choose PAID instead.`;
    }
    if (status === 'full' && willBe <= 0 && feeRate > 0) {
      return 'PAID with nothing collected is a CREDIT — choose that instead.';
    }
    if (reason.trim().length < MIN_REASON) {
      return `Give a reason of at least ${MIN_REASON} characters — `
           + `${MIN_REASON - reason.trim().length} more to go.`;
    }
    return '';
  }, [changed, status, willBe, feeRate, reason]);

  const save = async () => {
    if (problem || busy) return;
    const ok = await swal.confirm({
      title: 'Correct this day?',
      tone: cashEffect < 0 ? 'danger' : 'warning',
      html: `<b>${studentName || studentId}</b> &middot; ${prettyType(incomeType)}`
          + `<br/>${longDate(date)}<br/><br/>`
          + `${(wasStatus || 'unmarked').toUpperCase()} ${money(wasAmount)} `
          + `&rarr; <b>${status.toUpperCase()} ${money(willBe)}</b>`
          + (wasPosted
              ? `<br/><br/>The old receipt will be <b>reversed</b> and a new one raised.<br/>`
                + (Math.abs(cashEffect) < 0.009
                    ? 'The cash book nets to zero.'
                    : cashEffect > 0
                      ? `<b style="color:#15803d">GH¢ ${money(cashEffect)} will be ADDED to the cash book.</b>`
                      : `<b style="color:#b91c1c">GH¢ ${money(Math.abs(cashEffect))} will be TAKEN OUT of the cash book.</b>`)
              : '<br/><br/>Nothing had been approved, so no money will move.'),
      confirmText: 'Yes, correct it',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await API.post('/collections/corrections', {
        student_id: studentId,
        income_type: incomeType,
        date,
        to_status: status,
        to_amount: willBe,
        reason: reason.trim(),
      });
      swal.success(r.data?.message || 'Corrected');
      onClose(); onDone?.();
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not correct this day'));
    } finally { setBusy(false); }
  };

  const outOfWindow = win && date < win.earliest;
  const locked = win?.period_locked;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ p: 0 }}>
        <Stack direction="row" spacing={1.5} alignItems="center"
          sx={{ px: 2.2, py: 1.6, bgcolor: '#fdf8ed', borderBottom: '1px solid #f3e0ba' }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
            bgcolor: '#d99b20', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <CorrectIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 17, fontWeight: 800, color: '#a16207', lineHeight: 1.25 }}>
              Correct {studentName || studentId}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: '#8a7550' }}>
              {prettyType(incomeType)}{className ? ` · ${className}` : ''} · {longDate(date)}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {locked && (
          <Alert severity="error" icon={<WarnIcon />} sx={{ mb: 1.6, fontSize: 12 }}>
            The accounting period <b>{win.period_name}</b> is locked, so nothing in it can
            be changed. Unlock it under Settings → Finance → Accounting Periods if this
            correction really has to be made.
          </Alert>
        )}
        {outOfWindow && !locked && (
          <Alert severity="error" icon={<WarnIcon />} sx={{ mb: 1.6, fontSize: 12 }}>
            {longDate(date)} is outside the correction window — collections can only be
            corrected back to <b>{longDate(win.earliest)}</b>, the current month and the
            one before it. Anything older has been reported on and must be adjusted
            through the ledger instead.
          </Alert>
        )}

        {/* What it says now. */}
        <Card elevation={0} sx={{
          p: 1.4, mb: 1.8, borderRadius: 2,
          bgcolor: '#f7fafd', border: '1px solid #e3eaf3',
        }}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#64748b', mb: 0.4 }}>
            IT SAYS NOW
          </Typography>
          <Stack direction="row" spacing={1.2} alignItems="baseline" flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>
              {(wasStatus || 'Not marked').toUpperCase()}
            </Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#0f172a', ...TNUM }}>
              GH¢ {money(wasAmount)}
            </Typography>
            <Chip size="small"
              label={wasPosted ? 'Approved — money posted' : 'Not yet approved'}
              sx={{
                height: 21, fontSize: 10, fontWeight: 700,
                bgcolor: wasPosted ? '#dff3e7' : '#eef2f7',
                color: wasPosted ? '#15803d' : '#64748b',
              }} />
            {Number(cell?.credit_used || 0) > 0 && (
              <Typography sx={{ fontSize: 11, color: '#4338ca', fontWeight: 700 }}>
                GH¢ {money(cell.credit_used)} of it came from advance credit
              </Typography>
            )}
          </Stack>
        </Card>

        {/* What it should say. */}
        <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: '#334155', mb: 0.9 }}>
          IT SHOULD SAY
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.6 }}>
          {CHOICES.map(c => {
            const on = status === c.status;
            return (
              <Box key={c.status} onClick={() => pick(c.status)}
                sx={{
                  px: 1.3, py: 1, borderRadius: 2, cursor: 'pointer',
                  bgcolor: c.bg,
                  border: `${on ? 2 : 1}px solid ${on ? c.fg : c.line}`,
                  '&:hover': { borderColor: c.fg },
                }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: c.fg }}>
                  {c.word}
                </Typography>
                <Typography sx={{ fontSize: 10, color: '#64748b', lineHeight: 1.45 }}>
                  {c.hint}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Stack direction="row" spacing={1.4} alignItems="center" sx={{ mb: 1.6 }}>
          <TextField size="small" type="number" label="Amount collected (GH¢)"
            value={amount} onChange={(e) => setAmount(e.target.value)}
            disabled={status === 'absent' || status === 'none'}
            inputProps={{ min: 0, step: '0.01', style: { ...TNUM, fontWeight: 700 } }}
            sx={{ width: 200 }} />
          <Typography sx={{ fontSize: 11, color: '#64748b' }}>
            The day's rate is <b>GH¢ {money(feeRate)}</b>.
            {(status === 'absent' || status === 'none')
              && ' Nothing is collected on this status.'}
          </Typography>
        </Stack>

        {/* What it will do to the money. The point of the dialog. */}
        <Card elevation={0} sx={{
          p: 1.5, mb: 1.6, borderRadius: 2,
          bgcolor: Math.abs(cashEffect) < 0.009 ? '#f7fafd'
                 : cashEffect > 0 ? '#f1faf5' : '#fdf3f3',
          border: `1px solid ${Math.abs(cashEffect) < 0.009 ? '#e3eaf3'
                 : cashEffect > 0 ? '#cfe7d6' : '#f6cccc'}`,
        }}>
          <Stack direction="row" spacing={1.3} alignItems="center">
            {Math.abs(cashEffect) < 0.009
              ? <FlatIcon sx={{ fontSize: 26, color: '#94a3b8' }} />
              : cashEffect > 0
                ? <UpIcon sx={{ fontSize: 26, color: '#15803d' }} />
                : <DownIcon sx={{ fontSize: 26, color: '#b91c1c' }} />}
            <Box>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#64748b' }}>
                EFFECT ON THE CASH BOOK
              </Typography>
              <Typography sx={{
                fontSize: 19, fontWeight: 800, ...TNUM,
                color: Math.abs(cashEffect) < 0.009 ? '#64748b'
                     : cashEffect > 0 ? '#15803d' : '#b91c1c',
              }}>
                {Math.abs(cashEffect) < 0.009 ? 'No change'
                  : `${cashEffect > 0 ? '+' : '−'} GH¢ ${money(Math.abs(cashEffect))}`}
              </Typography>
              <Typography sx={{ fontSize: 10.5, color: '#64748b' }}>
                {wasPosted
                  ? 'The old receipt is reversed and a new one raised — both stay on the ledger.'
                  : 'Nothing was approved, so no receipt exists to reverse.'}
              </Typography>
            </Box>
          </Stack>
        </Card>

        <Divider sx={{ mb: 1.6 }} />

        <TextField fullWidth multiline minRows={2} size="small"
          label="Why is this being corrected?"
          placeholder="e.g. marked on the wrong learner — the money was Ama's, not Kofi's"
          value={reason} onChange={(e) => setReason(e.target.value)}
          helperText={`At least ${MIN_REASON} characters. This is the only record of why the figure moved.`}
          FormHelperTextProps={{ sx: { fontSize: 10.5 } }} />
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.4, borderTop: '1px solid #e8eef6' }}>
        {problem && (
          <Typography sx={{ fontSize: 11, color: '#a16207', flex: 1, fontWeight: 600 }}>
            {problem}
          </Typography>
        )}
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={save}
          disabled={!!problem || busy || outOfWindow || locked}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <CorrectIcon />}
          sx={{
            textTransform: 'none', fontWeight: 700, borderRadius: 1.5,
            bgcolor: '#d99b20', '&:hover': { bgcolor: '#a16207' },
          }}>
          Correct this day
        </Button>
      </DialogActions>
    </Dialog>
  );
}
